import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ViewerSettingSpaceColorType} from '../../../../../../design-system/organisms/ViewerSetting/ViewerSettingSpaceColorItemType.interface';
import {ImageDownloadData} from '../../../../../../graphql/generated/types';
import {
  IMAGE_VIEWER_STATE_NAME,
  IS_TWO_PAGE_LAYOUT_DEFAULT,
  SPACE_COLOR_DEFAULT,
} from '../constant/viewerConstant';
import {ImageViewerReadType} from '../type/ImageViewerReadType';
import {ImageViewerState} from './imageViewerState.interface';

export const imageViewerSlice = createSlice({
  name: IMAGE_VIEWER_STATE_NAME,
  initialState: {
    settings: {
      readType: ImageViewerReadType.Page,
      spaceColor: SPACE_COLOR_DEFAULT,
      isTwoPageLayout: IS_TWO_PAGE_LAYOUT_DEFAULT,
    },
    viewData: {},
  } as ImageViewerState,
  reducers: {
    changeReadType: (state, action: PayloadAction<ImageViewerReadType>) => {
      state.settings.readType = action.payload;
    },
    changeSpaceColor: (
      state,
      action: PayloadAction<ViewerSettingSpaceColorType>
    ) => {
      state.settings.spaceColor = action.payload;
    },
    changeTwoPageLayout: (state, action: PayloadAction<boolean>) => {
      state.settings.isTwoPageLayout = action.payload;
    },
    toggleTwoPageLayout: state => {
      state.settings.isTwoPageLayout = !state.settings.isTwoPageLayout;
    },
    setDownloadData: (
      state,
      action: PayloadAction<{
        productId: number;
        downloadData: ImageDownloadData;
      }>
    ) => {
      const {productId, downloadData} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.downloadData = downloadData;
      state.viewData[productId] = productViewData;
    },
    setCurrentPage: (
      state,
      action: PayloadAction<{
        productId: number;
        page: number;
        isBySeekbar?: boolean;
      }>
    ) => {
      const {productId, page, isBySeekbar} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.currentPage = page;
      productViewData.isPageChangedBySeekbar = isBySeekbar ?? false;
      state.viewData[productId] = productViewData;
    },
    setTotalPage: (
      state,
      action: PayloadAction<{
        productId: number;
        totalPage: number;
      }>
    ) => {
      const {productId, totalPage} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.totalPage = totalPage;
      state.viewData[productId] = productViewData;
    },
    setIsViewerEnd: (
      state,
      action: PayloadAction<{
        productId: number;
        isViewerEnd: boolean;
      }>
    ) => {
      const {productId, isViewerEnd} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.isViewerEnd = isViewerEnd;
      state.viewData[productId] = productViewData;
    },
    setIsProgressBarExpanded: (
      state,
      action: PayloadAction<{
        productId: number;
        isProgressBarExpanded: boolean;
      }>
    ) => {
      const {productId, isProgressBarExpanded} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.isProgressBarExpanded = isProgressBarExpanded;
      state.viewData[productId] = productViewData;
    },
    setIsHandlingSeekBar: (
      state,
      action: PayloadAction<{
        productId: number;
        isHandling: boolean;
      }>
    ) => {
      const {productId, isHandling} = action.payload;
      const productViewData = state.viewData[productId] || {};
      productViewData.isHandlingSeekBar = isHandling;
      state.viewData[productId] = productViewData;
    },
  },
});

export const {
  changeReadType,
  changeSpaceColor,
  changeTwoPageLayout,
  toggleTwoPageLayout,
  setDownloadData,
  setCurrentPage,
  setTotalPage,
  setIsViewerEnd,
  setIsProgressBarExpanded,
  setIsHandlingSeekBar,
} = imageViewerSlice.actions;
